import React from 'react';
import {
  LogOut, Shield, Package, Truck, LayoutDashboard, Search, Building2, CalendarDays
} from 'lucide-react';

/** Eén knop in de mobiele onderbalk, aangestuurd vanuit de pagina zelf. */
export interface MobileNavItem {
  id:    string;
  icon:  React.ElementType;
  label: string;
}

interface LayoutProps {
  children: React.ReactNode;
  userName: string;
  userRole: string;
  onLogout: () => void;
  hideMobileNav?: boolean;
  extraHeaderContent?: React.ReactNode;
  /** Maakt de onderbalk klikbaar; zonder deze prop blijft hij louter indicatief. */
  mobileNav?: {
    items:    MobileNavItem[];
    activeId: string;
    onSelect: (id: string) => void;
  };
}

const NAV_ITEMS: Record<string, { icon: React.ElementType; label: string }[]> = {
  SUPERUSER:  [{ icon: LayoutDashboard, label: 'Dashboard' }],
  ADMIN:      [{ icon: Package,         label: 'Pakketten' }],
  APOTHEEK:   [{ icon: Package,         label: 'Pakketten' }],
  KOERIER:    [{ icon: Truck,           label: 'Mijn Rit'  }],
  SUPERVISOR: [{ icon: LayoutDashboard, label: 'Logboek'   }],
  PATIENT:    [{ icon: Search,          label: 'Traceren'  }],
  // De sleutels zijn de UserRole-waarden; die van deze twee zijn de
  // database-schrijfwijze (zie de enum in types.ts).
  region_manager: [{ icon: Building2,    label: 'Mijn apotheken' }],
  planner:        [{ icon: CalendarDays, label: 'Planner'        }],
};

const ROLE_LABELS: Record<string, string> = {
  SUPERUSER:  'Superuser',
  ADMIN:      'Admin',
  APOTHEEK:   'Apotheek',
  KOERIER:    'Koerier',
  SUPERVISOR: 'Supervisor',
  PATIENT:    'Patiënt',
  region_manager: 'Regiomanager',
  planner:        'Planner',
};

const Layout: React.FC<LayoutProps> = ({
  children,
  userName,
  userRole,
  onLogout,
  hideMobileNav = false,
  extraHeaderContent,
  mobileNav,
}) => {
  const navItems = NAV_ITEMS[userRole] ?? [];
  const roleLabel = ROLE_LABELS[userRole] ?? userRole;

  if (hideMobileNav) {
    return <>{children}</>;
  }

  return (
    <div className="flex h-dvh bg-[#f7f9fb]">

      {/* ── Sidebar (desktop only) — alle breedte/positie-modifiers met lg: zodat
            de aside op mobiel geen layout kan verstoren, ook bij specificiteit-bugs ── */}
      <aside className="hidden lg:flex lg:flex-col lg:w-60 lg:shrink-0 lg:sticky lg:top-0 lg:h-[100svh] bg-white overflow-y-auto z-30"
        style={{ boxShadow: '4px 0 24px rgba(25,28,30,0.04)' }}>

        {/* Branding */}
        <div className="px-6 py-6">
          {/* Logo en subtitel vullen samen exact de beschikbare breedte. De
              subtitel is van zichzelf smaller dan het logo; text-align-last
              met inter-character spreidt hem uit tot dezelfde breedte. De
              tracking van 0.2em is de terugval voor browsers die text-justify
              negeren: dan blijft er 12 px over in de woordspatie in plaats
              van 33 px. */}
          <div className="w-full">
            <img src="/gobob-logo-horizontal.png" alt="GoBob" className="max-w-full h-auto" />
            <p className="block w-full mt-2 text-[10px] font-body text-[#3d4945] uppercase tracking-[0.2em] -mr-[0.2em] text-justify [text-align-last:justify] [text-justify:inter-character]">
              Apotheek bezorgservice
            </p>
          </div>
        </div>

        {/* Nav items */}
        <nav className="flex-1 px-3 py-2 space-y-1">
          {navItems.map((item, i) => (
            <div
              key={item.label}
              className={`flex items-center space-x-3 px-3 py-2.5 rounded-xl transition-colors ${
                i === 0 ? 'text-[#006b5a]' : 'text-[#3d4945]'
              }`}
              style={i === 0 ? { background: 'linear-gradient(135deg, rgba(0,107,90,0.08), rgba(72,194,169,0.08))' } : {}}
            >
              <item.icon size={18} className="shrink-0" />
              <span className={`text-sm ${i === 0 ? 'font-display font-bold' : 'font-body'}`}>{item.label}</span>
            </div>
          ))}
        </nav>

        {/* User + logout */}
        <div className="px-4 py-5 space-y-3">
          <div className="px-2">
            <p className="text-sm font-display font-black text-[#191c1e] truncate">{userName}</p>
            <p className="text-[10px] font-body text-[#3d4945] uppercase tracking-widest mt-0.5">{roleLabel}</p>
          </div>
          <button
            onClick={onLogout}
            className="w-full flex items-center space-x-2 px-3 py-2.5 rounded-xl text-sm font-body text-[#3d4945] hover:bg-red-50 hover:text-red-600 transition-all"
          >
            <LogOut size={16} />
            <span>Uitloggen</span>
          </button>
        </div>
      </aside>

      {/* ── Main content — w-full + min-w-0 zodat op mobiel altijd 100vw breed ── */}
      <div className="flex-1 flex flex-col min-w-0 w-full">

        {/* Header */}
        <header className="sticky top-0 z-40 h-14 flex items-center px-4 lg:px-6 gap-3"
          style={{ background: 'rgba(255,255,255,0.80)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', boxShadow: '0 4px 24px rgba(25,28,30,0.04)' }}>

          {/* Logo: mobile only */}
          <div className="flex items-center lg:hidden shrink-0">
            <img src="/gobob-logo-horizontal.png" alt="GoBob" className="h-8 w-auto" />
          </div>

          {/* Extra content */}
          <div className="flex-1 flex items-center justify-end lg:justify-start gap-2 min-w-0">
            {extraHeaderContent}
          </div>

          {/* User + logout: mobile only */}
          <div className="flex items-center gap-2 lg:hidden shrink-0">
            <div className="text-right hidden xs:block">
              <p className="text-[11px] font-display font-black text-[#191c1e] leading-none truncate max-w-[96px]">{userName}</p>
              <p className="text-[9px] font-body text-[#3d4945] uppercase tracking-tighter">{roleLabel}</p>
            </div>
            <button
              onClick={onLogout}
              aria-label="Uitloggen"
              className="w-9 h-9 bg-[#f2f4f6] rounded-xl flex items-center justify-center text-[#3d4945] hover:bg-red-50 hover:text-red-600 transition-all shrink-0"
            >
              <LogOut size={18} />
            </button>
          </div>
        </header>

        {/* Page content — overflow-y-auto vangt scroll af i.p.v. body/window */}
        <main className="flex-1 overflow-y-auto px-4 py-5 lg:px-8 lg:py-8 max-w-6xl mx-auto w-full touch-pan-y">
          {children}
        </main>

        {/* Footer: desktop only */}
        <footer className="hidden lg:block py-8 text-center text-[#3d4945]/60 text-xs">
          <div className="flex items-center justify-center space-x-2 mb-1">
            <Shield size={13} className="text-[#006b5a]" />
            <span>AVG-Compliant &amp; Real-time Cloud Persistence</span>
          </div>
          <p>© 2025 GoBob. Alle data is E2E geëncrypteerd.</p>
        </footer>
      </div>

      {/* ── Bottom nav (mobile) ── */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-40 pb-safe flex items-stretch"
        style={{ background: 'rgba(247,249,251,0.80)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', boxShadow: '0 -4px 24px rgba(25,28,30,0.04)' }}>
        {mobileNav
          ? mobileNav.items.slice(0, 3).map(item => (
              <button
                key={item.id}
                onClick={() => mobileNav.onSelect(item.id)}
                className={`flex-1 flex flex-col items-center justify-center py-3 gap-0.5 transition-colors ${
                  mobileNav.activeId === item.id ? 'text-[#006b5a]' : 'text-[#3d4945]/60'
                }`}
              >
                <item.icon size={22} />
                <span className="text-[10px] font-display font-bold leading-none">{item.label}</span>
              </button>
            ))
          : navItems.slice(0, 3).map((item, i) => (
              <div
                key={item.label}
                className={`flex-1 flex flex-col items-center justify-center py-3 gap-0.5 ${
                  i === 0 ? 'text-[#006b5a]' : 'text-[#3d4945]/60'
                }`}
              >
                <item.icon size={22} />
                <span className="text-[10px] font-display font-bold leading-none">{item.label}</span>
              </div>
            ))}
        <button
          onClick={onLogout}
          className="flex-1 flex flex-col items-center justify-center py-3 gap-0.5 text-[#3d4945]/60 hover:text-red-500 active:text-red-600 transition-colors"
        >
          <LogOut size={22} />
          <span className="text-[10px] font-display font-bold leading-none">Uitloggen</span>
        </button>
      </nav>

      {/* Spacer for mobile bottom nav */}
      <div className="lg:hidden h-16 pb-safe" aria-hidden />
    </div>
  );
};

export default Layout;
